if (!customElements.get("age-verification")) {
  customElements.define(
    "age-verification",
    class AgeVerification extends ModalDialog {
      constructor() {
        super();

        this.delay = parseInt(this.dataset.delay, 10) * 1000;
        this.id = this.getAttribute("id");
        this.storageName = this.dataset.storageName;
        this.storageExpiration = parseInt(this.dataset.storageExpiration, 10) || 30;
        // MARK:- Age gate: permanent storage, "No" redirect target, dismissal gating
        this.storagePermanent = this.dataset.storagePermanent === "true";
        this.redirectUrl = this.querySelector(".age-verification-buttons")?.dataset.redirectUrl;
        // Tolerate protocol-less addresses like "google.com" (a relative "/google.com" would 404)
        if (this.redirectUrl && !/^(https?:|\/|#)/i.test(this.redirectUrl)) {
          this.redirectUrl = "https://" + this.redirectUrl;
        }
        // MARK:- Fail open — gate dismissals only when a Yes button exists; a section
        // without a buttons block must never lock the page
        this.allowDismiss = !this.querySelector(".age-verification-button-yes");
        this.answered = false;

        // MARK:- Fail open — track the gate across the mobile breakpoint: release the
        // scroll lock when "Hide on mobile" hides an open gate, and show a skipped,
        // still-unanswered gate when the viewport widens back to desktop
        if (this.classList.contains("hidden-mobile") && window.matchMedia) {
          window.matchMedia("(max-width: 749px)").addEventListener("change", (event) => {
            if (Shopify.designMode) {
              return;
            }

            if (event.matches) {
              if (this.hasAttribute("open")) {
                const wasAllowed = this.allowDismiss;
                this.allowDismiss = true;
                this.hide();
                this.allowDismiss = wasAllowed;
              }
              return;
            }

            if (this.hasAttribute("open") || this.answered || !this.yesButton) {
              return;
            }
            if (this.storageName && this.isVerified()) {
              return;
            }
            if (window.getComputedStyle(this).display === "none") {
              return;
            }
            this.show();
          });
        }
        this.dialogWrap = this.querySelector(".modal-dialog-wrap");

        this.yesButton = this.querySelector(".age-verification-button-yes");
        this.noButton = this.querySelector(".age-verification-button-no");
        this.rejectionMessage = this.querySelector(".age-verification-rejection-message");

        if (this.yesButton) {
          this.yesButton.addEventListener("click", this.handleYesClick.bind(this));
        }

        if (this.noButton) {
          this.noButton.addEventListener("click", this.handleNoClick.bind(this));
        }

        // Replace the close button to remove any existing event listeners
        this.setupCloseButton();
      }

      // Set up the close button with our custom behavior
      setupCloseButton() {
        const closeButton = this.querySelector('[id^="ModalClose-"]');
        if (closeButton) {
          // Clone the button to remove all existing event listeners
          const newCloseButton = closeButton.cloneNode(true);
          closeButton.parentNode.replaceChild(newCloseButton, closeButton);

          // Add our event listener to the new button
          newCloseButton.addEventListener("click", (e) => {
            e.preventDefault();
            // MARK:- The opt-in close button is an explicit merchant choice — allow dismissal
            this.allowDismiss = true;
            this.hide();
          });
        }
      }

      connectedCallback() {
        if (this.storageName && this.isVerified() && !Shopify.designMode) {
          return;
        }

        // Show the age verification after the specified delay
        if (!Shopify.designMode) {
          setTimeout(() => {
            // MARK:- Fail open — a gate that cannot be answered (no Yes button) must not open
            if (!this.yesButton) {
              return;
            }
            // MARK:- Fail open — never lock scroll behind a gate CSS has hidden (e.g. "Hide on mobile")
            if (window.getComputedStyle(this).display === "none") {
              return;
            }
            this.show();
          }, this.delay);
        }

        return super.connectedCallback();
      }

      handleYesClick() {
        // Save verification to localStorage
        if (this.storageName) {
          this.saveVerification();
        }
        this.answered = true;
        this.allowDismiss = true;
        this.hide();

        const event = new CustomEvent("age-verification:verified", {
          detail: {
            id: this.id,
          },
        });
        document.dispatchEvent(event);
      }

      handleNoClick() {
        const event = new CustomEvent("age-verification:rejected", {
          detail: {
            id: this.id,
          },
        });
        document.dispatchEvent(event);

        // MARK:- Redirect away when a redirect URL is configured; otherwise show the rejection message
        if (this.redirectUrl) {
          window.location.href = this.redirectUrl;
          return;
        }

        if (this.rejectionMessage) {
          this.rejectionMessage.style.display = "block";
        }
      }

      show() {
        super.show();

        // MARK:- Move initial focus to the Yes button — the base class focuses the
        // non-focusable dialog div, which leaves the focus trap unarmed
        if (this.yesButton) {
          trapFocus(this, this.yesButton);
        }

        const event = new CustomEvent("age-verification:open", {
          detail: {
            id: this.id,
          },
        });
        document.dispatchEvent(event);
      }

      hide() {
        // MARK:- The gate must be answered — block dismissals (e.g. Escape key) until Yes or the opt-in close button
        if (!this.allowDismiss && !Shopify.designMode) {
          return;
        }

        if (!this.dialogWrap) {
          // Fallback if dialogWrap is not available
          super.hide();
          return;
        }

        setTimeout(() => {
          ModalDialog.prototype.hide.call(this);
          const event = new CustomEvent("age-verification:close", {
            detail: {
              id: this.id,
            },
          });
          document.dispatchEvent(event);
        }, 200);
      }

      // Save verification to localStorage with expiration
      saveVerification() {
        try {
          const data = {
            verified: true,
          };

          // MARK:- Permanent storage omits the expiry stamp — isVerified() treats a missing "expires" as never expiring
          if (!this.storagePermanent) {
            const now = new Date();
            const expirationDate = new Date(now);
            expirationDate.setDate(now.getDate() + this.storageExpiration);
            data.expires = expirationDate.getTime();
          }

          localStorage.setItem(this.storageName, JSON.stringify(data));
        } catch (e) {
          console.error("Error saving to localStorage:", e);
        }
      }

      // Check if user is verified and verification hasn't expired
      isVerified() {
        try {
          const storedData = localStorage.getItem(this.storageName);
          if (!storedData) return false;

          const data = JSON.parse(storedData);
          const now = new Date().getTime();

          // MARK:- Reconcile the stored record with the current storage mode:
          // permanent mode ignores stale expiry stamps; expiring mode re-asks
          // visitors holding a permanent record
          if (this.storagePermanent) {
            return data.verified === true;
          }

          if (!data.expires || data.expires < now) {
            // Clear expired or mode-mismatched verification
            localStorage.removeItem(this.storageName);
            return false;
          }

          return data.verified === true;
        } catch (e) {
          console.error("Error reading from localStorage:", e);
          return false;
        }
      }
    },
  );
}
